import { identityDocumentFileError, INVALID_IDENTITY_DOCUMENT_MESSAGE } from "./identityDocumentFiles";

export type IdentityPhotoSource = "gallery" | "camera";
const READ_TIMEOUT_MS = 30_000;

function errorMessage(failure: unknown): string {
  return typeof failure === "object" && failure !== null && "message" in failure && typeof failure.message === "string"
    ? failure.message : "";
}

function checkReadableImage(blob: Blob, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(blob);
    function finish(failure?: Error) {
      image.onload = null; image.onerror = null;
      signal.removeEventListener("abort", abort);
      image.src = "";
      URL.revokeObjectURL(url);
      failure ? reject(failure) : resolve();
    }
    function abort() { finish(new DOMException("Photo read cancelled.", "AbortError")); }
    image.onload = () => finish(image.naturalWidth && image.naturalHeight ? undefined : new Error("Unreadable photo."));
    image.onerror = () => finish(new Error("Unreadable photo."));
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
    else image.src = url;
  });
}

/** Native 8.0.x converts gallery/camera images (including HEIC) to oriented JPEGs.
 * Read full-resolution bytes, never the thumbnail, and keep evidence out of the
 * public gallery. Cancellation returns null; source/read failures throw safe
 * recovery messages. The caller's guard must span selection through upload
 * admission, and its AbortError is preserved when that ownership changes. */
export async function pickIdentityPhoto(source: IdentityPhotoSource, assertCurrent: () => void, signal: AbortSignal): Promise<File | null> {
  let timer: number | undefined;
  const reading = new AbortController();
  const abort = () => reading.abort();
  signal.addEventListener("abort", abort, { once: true });
  try {
    const { Camera, CameraSource, CameraResultType } = await import("@capacitor/camera");
    assertCurrent();
    const photo = await Camera.getPhoto({
      source: source === "gallery" ? CameraSource.Photos : CameraSource.Camera,
      resultType: CameraResultType.Uri, quality: 100, correctOrientation: true,
      allowEditing: false, saveToGallery: false,
    });
    assertCurrent();
    if (!photo.webPath || !["jpeg", "jpg", "png", "webp"].includes(photo.format?.toLowerCase())) throw new Error("Unreadable photo.");
    timer = window.setTimeout(abort, READ_TIMEOUT_MS);
    // This is the plugin's local file URL; never attach the member's API credentials.
    const response = await fetch(photo.webPath, { signal: reading.signal, credentials: "omit", cache: "no-store" });
    if (!response.ok) throw new Error("Unreadable photo.");
    const bytes = await response.blob();
    assertCurrent();
    const extension = photo.format.toLowerCase() === "jpg" ? "jpeg" : photo.format.toLowerCase();
    const file = new File([bytes], `identity-${source}.${extension}`, { type: `image/${extension}` });
    const invalid = identityDocumentFileError(file);
    if (invalid) throw new Error(invalid);
    await checkReadableImage(file, reading.signal);
    assertCurrent();
    return file;
  } catch (failure) {
    assertCurrent();
    const message = errorMessage(failure);
    if (/\bcancell?ed\b/i.test(message) && !reading.signal.aborted) return null;
    if (/denied|restricted|permission/i.test(message)) throw new Error(`Allow ${source === "camera" ? "camera" : "photo"} access for KontrataPH in device Settings, then try again, or choose a file.`);
    if (/not available|unavailable|not implemented|no camera/i.test(message)) throw new Error(`The ${source === "camera" ? "camera" : "gallery"} is unavailable. Try another source or choose a file.`);
    if (message === INVALID_IDENTITY_DOCUMENT_MESSAGE) throw new Error(message);
    throw new Error("Could not read the photo. Try again or choose a JPEG, PNG, WebP or PDF file up to 25 MB.");
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", abort);
  }
}
